// 'aod_prop' : { variable: "Access to Public Open Space", hoverInfo: "Proportion of the population that lives within an Area of Deficiency in Access to Local and District Public Open Space. Area of Deficiency is defined by Greenspace Information for Greater London CIC based on distances to public open space in the London Plan.<br><br>Threshold is based on population in an AoD greater than the London average."},

import React, { useState, useCallback } from 'react'
import DeckGL from '@deck.gl/react'
import { MVTLayer } from '@deck.gl/geo-layers'
import {
  _MapContext as MapContext,
  NavigationControl,
  StaticMap,
  Marker
} from 'react-map-gl'
import MatGeocoder from 'react-mui-mapbox-geocoder'
import { makeStyles } from '@material-ui/core/styles'
import { MapView } from '@deck.gl/core'
import IconButton from '@material-ui/core/IconButton'
import MARKER_STYLE from './marker-style'
// import usePersistedState from './usePersistedState'
import clsx from 'clsx'
import Drawer from '@material-ui/core/Drawer'
import AppBar from '@material-ui/core/AppBar'
import Toolbar from '@material-ui/core/Toolbar'
import CssBaseline from '@material-ui/core/CssBaseline'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import MenuIcon from '@material-ui/icons/Menu'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import CloseIcon from '@material-ui/icons/Close'
import { useQueryParam, StringParam } from 'use-query-params'
import { pack, unpack } from 'jcb64'

const drawerWidth = 240

const _renderMarker = (place, i) => {
  const { name, coordinates } = place
  return (
    <Marker
      key={i}
      longitude={coordinates[0]}
      latitude={coordinates[1]}
      captureDrag={false}
      captureDoubleClick={false}
    >
      <div className="place">
        <span>{name}</span>
      </div>
    </Marker>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    width: '100%',
    '& > * + *': {
      marginTop: theme.spacing(2)
    }
  },
  geocoder: {
    zIndex: 4,
    textAlign: 'center',
    margin: '1rem auto',
    width: '80%'
  },
  appBar: {
    backgroundColor: 'unset',
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  appBarShift: {
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    marginRight: drawerWidth
  },
  title: {
    flexGrow: 1,
    overflow: 'visible'
  },
  hide: {
    display: 'none'
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0
  },
  drawerPaper: {
    width: drawerWidth
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-start'
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    marginRight: -drawerWidth
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    marginRight: 0
  }
}))

const INITIAL_VIEW_STATE = {
  longitude: -0.03,
  latitude: 51.49,
  zoom: 9,
  pitch: 10,
  bearing: 0,
  minZoom: 9,
  maxZoom: 17
}

export default function App({ showBorder = false, onTilesLoad = null }) {
  const classes = useStyles()

  const MAPBOX_TOKEN =
    'pk.eyJ1IjoiZ2xhLWdpcyIsImEiOiJjanBvNGh1bncwOTkzNDNueWt5MGU1ZGtiIn0.XFxLdq2dXttcXSXTiREPTA'

  const getTooltip = ({ object }) => {
    return (
      object && {
        html: `\
        <div>Access to Public Open Space score: ${
          object.properties.aod_prop !== -999
            ? object.properties.aod_prop
            : 'n/a'
        }</div>`
      }
    )
  }

  const aodLayer = new MVTLayer({
    // data: `https://circular-attraction.glitch.me/data/aod-hexes/{z}/{x}/{y}.pbf`,
    data: `https://apps.london.gov.uk/tileserver/data/hexes-aod/{z}/{x}/{y}.pbf`,
    minZoom: 9,
    maxZoom: 17,
    getLineColor: [224, 224, 224],
    getFillColor: d => {
      const val = d.properties.aod_prop * 255
      return [255, 255, 255, val]
    },
    pickable: true
  })

  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE)

  const onViewStateChange = useCallback(({ viewState }) => {
    setViewState(viewState)
  }, [])

  const geocoderApiOptions = {
    country: 'gb',
    proximity: { longitude: 0, latitude: 51.49 },
    bbox: [-0.489, 51.28, 0.236, 51.686]
  }

  // const [markers, setMarkers] = usePersistedState('markers', [])
  // const markersMemo = useMemo(() => markers, [markers])

  const [places, setPlaces] = useQueryParam('places', StringParam)

  const onSelectHandler = result => {
    // setMarkers(
    //   markers.concat([
    //     {
    //       name: result.text,
    //       coordinates: [result.center[0], result.center[1]]
    //     }
    //   ])
    // )
    const place = [
      {
        name: result.text,
        coordinates: [result.center[0], result.center[1]]
      }
    ]
    setPlaces(
      places && places.length > 0
        ? pack(unpack(places).concat(place))
        : pack(place),
      'push'
    )

    // if (markers.length > 0) {
    //   const fullUrl = document.location.href + pack(markers)
    //   if (fullUrl.length < 2047) {
    //     setPlaces(pack(markers))
    //   }
    // }

    setViewState({
      longitude: result.center[0],
      latitude: result.center[1],
      zoom: 14,
      transitionDuration: 600
    })
  }

  const [open, setOpen] = React.useState(true)

  const handleDrawerOpen = () => {
    setOpen(true)
  }

  const handleDrawerClose = () => {
    setOpen(false)
  }

  const deletePlace = place => {
    // setMarkers(markers.filter(el => el.name !== place.name))
    setPlaces(pack(unpack(places).filter(el => el.name !== place.name)), 'push')
  }

  const gotoPlace = place => {
    setViewState({
      longitude: place.coordinates[0],
      latitude: place.coordinates[1],
      zoom: 14,
      transitionDuration: 400
    })
  }

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar
        position="fixed"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: open
        })}
      >
        <Toolbar>
          <Typography variant="h6" noWrap className={classes.title}>
            <div className={classes.geocoder}>
              <MatGeocoder
                tabIndex={0}
                inputPlaceholder="Search Postcode or Address"
                accessToken={MAPBOX_TOKEN}
                onSelect={onSelectHandler}
                showLoader={true}
                focusOnMount={true}
                {...geocoderApiOptions}
              />{' '}
            </div>{' '}
          </Typography>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="end"
            onClick={handleDrawerOpen}
            className={clsx(open && classes.hide)}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <main
        className={clsx(classes.content, {
          [classes.contentShift]: open
        })}
      >
        <DeckGL
          ContextProvider={MapContext.Provider}
          layers={[aodLayer]}
          views={
            new MapView({
              controller: { doubleClickZoom: false },
              width: `${
                open
                  ? document.querySelector('#root').offsetWidth -
                    drawerWidth +
                    'px'
                  : '100%'
              }`,
              repeat: true
            })
          }
          viewState={viewState}
          onViewStateChange={onViewStateChange}
          getTooltip={getTooltip}
        >
          <style>{MARKER_STYLE}</style>
          {places && unpack(places).map(_renderMarker)}
          <StaticMap
            reuseMaps
            mapStyle="os_night.json"
            preventStyleDiffing={false}
            mapboxApiAccessToken={MAPBOX_TOKEN}
          ></StaticMap>
          <div
            style={{
              position: 'absolute',
              bottom: 10,
              left: 10,
              padding: '10px'
            }}
          >
            <NavigationControl />
          </div>
        </DeckGL>
      </main>
      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="right"
        open={open}
        classes={{
          paper: classes.drawerPaper
        }}
      >
        <div className={classes.drawerHeader}>
          <IconButton onClick={handleDrawerClose}>
            <ChevronRightIcon />
          </IconButton>
        </div>
        <Divider />

        <List>
          <div style={{ padding: '0 1rem' }}>
            <h3>Access to Public Open Space score</h3>
            <p>
              The score for each hex area shows the proportion of households
              within this area that are located in an Area of Deficiency in
              access to local and district public open space (AoD).
            </p>
            <p>
              The lighter areas of the map show where there is less access to
              public open space, and so a light cell with a score of 1 means
              that all households in that area are in an AoD.{' '}
            </p>
            <p>
              Cells with a value of <code>n/a</code> have either no households
              or data
            </p>
          </div>
          <Divider />
          <div style={{ padding: '0 1rem' }}>
            {places && unpack(places).length > 0 ? <h3>Saved places</h3> : ''}
            {places &&
              unpack(places).map((place, index) => (
                <ListItem button key={place.name}>
                  <ListItemIcon>
                    <CloseIcon
                      tabIndex={0}
                      onClick={() => deletePlace(place)}
                    />
                  </ListItemIcon>
                  <ListItemText
                    tabIndex={0}
                    primary={place.name}
                    onClick={() => gotoPlace(place)}
                  />
                </ListItem>
              ))}
          </div>
        </List>
      </Drawer>
    </div>
  )
}
